import * as React from "react";
import { useState } from "react";
import {
  useGetAllAdAccountsQuery,
  useUpdateAdAccountMutation,
} from "../../services/ad";
import { generateDynamicColumns } from "../../utils/generateDynamicColumns";
import { columnHelper } from "../../utils/utils";
import LoadingState from "../../components/states/LoadingState";
import TableTemplate from "../../components/common/TableTemplate";
import toast from "react-hot-toast";
import { PlusIcon } from "lucide-react";
import { Button } from "../../components/ui/button";
import { useSelector } from "react-redux";
import ErrorState from "../../components/states/ErrorState";
import { schemas, validateAndToast } from "../../utils/validation";
import { createFieldUpdater } from "../../utils/createFieldUpdater";

const AdAccountsPage = () => {
  const {
    data: adAccountsList,
    isLoading,
    isError,
    error,
  } = useGetAllAdAccountsQuery();
  const [updateAdAccount] = useUpdateAdAccountMutation();

  // State for tracking loading states of individual rows
  const [deletingRows, setDeletingRows] = useState({});
  const selectedRows = useSelector((state) => state.table.selectedRows);

  const columns = React.useMemo(
    () =>
      generateDynamicColumns({
        columnHelper,
        headerData: adAccountsList?.[0]?.Column_header,
        columnTypes: {
          rowSelectionColumn: adAccountsList?.[0]?.Column_header.row_checkbox,
          actionsColumn: adAccountsList?.[0]?.Column_header.row_actions,
        },
        actionConfig: {
          delete: {
            onClick: async (row) => {
              const rowId = row.id;
              const rowOriginal = row.original;
              try {
                setDeletingRows((prev) => ({ ...prev, [rowId]: true }));
                const payload = [
                  {
                    action: "delete_object",
                    object_glids: rowOriginal.glid.display,
                    object_class: "AdAccount",
                  },
                ];
                const result = await updateAdAccount(payload);
                if (result.error) {
                  const errorMessage =
                    result?.error?.data?.[0]?.response?.error?.message ||
                    result?.error?.data?.[0] ||
                    "Something went wrong";
                  toast.error(errorMessage);
                } else {
                  toast.success("Ad account deleted successfully");
                }
              } finally {
                setDeletingRows((prev) => ({}));
              }
            },
            isLoading: (rowId) => deletingRows[rowId],
          },
        },
        editableFields: {
          name: {
            placeholder: "Enter Account Name",
            onSave: createFieldUpdater(
              "AdAccount",
              "name",
              selectedRows,
              updateAdAccount
            ),
          },
          account_id: {
            placeholder: "Enter Account ID",
            onSave: createFieldUpdater(
              "AdAccount",
              "account_id",
              selectedRows,
              updateAdAccount
            ),
          },
          status: {
            onSave: createFieldUpdater(
              "AdAccount",
              "status",
              selectedRows,
              updateAdAccount
            ),
          },
          default_pixel: {
            onSave: createFieldUpdater(
              "AdAccount",
              "default_pixel",
              selectedRows,
              updateAdAccount
            ),
          },
          default_page: {
            onSave: createFieldUpdater(
              "AdAccount",
              "default_page",
              selectedRows,
              updateAdAccount
            ),
          },
          default_instagram: {
            onSave: createFieldUpdater(
              "AdAccount",
              "default_instagram",
              selectedRows,
              updateAdAccount
            ),
          },
          default_excluded_audience: {
            onSave: createFieldUpdater(
              "AdAccount",
              "default_excluded_audience",
              selectedRows,
              updateAdAccount
            ),
          },
          default_cta: {
            onSave: createFieldUpdater(
              "AdAccount",
              "default_cta",
              selectedRows,
              updateAdAccount
            ),
          },
          ai_optimizations: {
            onSave: createFieldUpdater(
              "AdAccount",
              "ai_optimizations",
              selectedRows,
              updateAdAccount
            ),
          },
        },
      }),
    [adAccountsList, deletingRows, updateAdAccount, selectedRows]
  );

  const handleCreateNewAdAccount = async () => {
    const payload = [
      {
        class: "AdAccount",
        glid: null,
        update: { name: "New Ad Account" },
      },
    ];
    const result = await updateAdAccount(payload);
    if (result.error) {
      const errorMessage =
        result?.error?.data?.[0]?.response?.error?.message ||
        result?.error?.data?.[0] ||
        "Something went wrong";
      toast.error(errorMessage);
    } else {
      toast.success("New ad account created successfully");
    }
  };

  if (isLoading) {
    return <LoadingState />;
  }

  if (isError) {
    return <ErrorState error={error} />;
  }

  return (
    <div className="space-y-4">
      <div
        style={{ backgroundColor: adAccountsList?.[0]?.Color }}
        className="p-2"
      >
        <h1 className="text-2xl font-bold text-gray-800">
          {adAccountsList?.[0]?.title || "Ad Accounts"}
        </h1>
        <h2 className="text-lg font-medium text-gray-800/75">
          {adAccountsList?.[0]?.["sub-title"] || "Manage your ad accounts"}
        </h2>
      </div>
      <div>
        <Button
          variant="ghost"
          size="default"
          className="outline outline-1"
          onClick={handleCreateNewAdAccount}
        >
          <PlusIcon className="w-4 h-4" />
          <span>New Ad Account</span>
        </Button>
      </div>
      <TableTemplate
        columns={columns}
        data={adAccountsList?.[0]?.data}
        options={{
          enableRowSelection: adAccountsList?.[0]?.Column_header.row_checkbox,
        }}
      />
    </div>
  );
};

export default AdAccountsPage;
